import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FileText,
  MessageSquare,
  LogOut,
  Menu,
  X,
  Search,
  PenTool,
  Plus,
  ChevronLeft,
  Briefcase,
  Clock,
  CheckCircle,
  AlertCircle,
  Home,
} from 'lucide-react';
import { Logo } from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ClientDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeView, setActiveView] = useState('main'); // main, new-case, inquiry, consultation, notary
  const [cases, setCases] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // نموذج قضية جديدة
  const [caseForm, setCaseForm] = useState({
    case_type: '',
    title: '',
    description: '',
    opponent_name: '',
    court_name: '',
    id_number: '',
  });

  // نموذج استشارة
  const [consultationForm, setConsultationForm] = useState({
    subject: '',
    message: '',
  });

  // نموذج خدمات الموثق
  const [notaryForm, setNotaryForm] = useState({
    service_type: '',
    description: '',
    parties_involved: '',
  });

  // بحث عن طلب
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setUser(JSON.parse(userStr));
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [casesRes, consultationsRes] = await Promise.all([
        axios.get(`${API}/cases`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/consultations`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setCases(casesRes.data);
      setConsultations(consultationsRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  // إرسال قضية جديدة
  const submitNewCase = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/client-requests`, {
        request_type: 'case',
        ...caseForm,
        client_id: user?.id,
        client_name: user?.full_name,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('تم إرسال طلبك بنجاح! سيتم التواصل معك قريباً');
      setCaseForm({ case_type: '', title: '', description: '', opponent_name: '', court_name: '', id_number: '' });
      setActiveView('main');
      fetchData();
    } catch (err) {
      alert('حدث خطأ أثناء إرسال الطلب');
    }
    setLoading(false);
  };

  // إرسال استشارة
  const submitConsultation = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/consultations`, consultationForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('تم إرسال طلب الاستشارة بنجاح!');
      setConsultationForm({ subject: '', message: '' });
      setActiveView('main');
      fetchData();
    } catch (err) {
      alert('حدث خطأ أثناء إرسال الاستشارة');
    }
    setLoading(false);
  };

  // إرسال طلب موثق
  const submitNotaryRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/client-requests`, {
        request_type: 'notary',
        ...notaryForm,
        client_id: user?.id,
        client_name: user?.full_name,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('تم إرسال طلب خدمة الموثق بنجاح!');
      setNotaryForm({ service_type: '', description: '', parties_involved: '' });
      setActiveView('main');
    } catch (err) {
      alert('حدث خطأ أثناء إرسال الطلب');
    }
    setLoading(false);
  };

  // فلترة الطلبات للبحث
  const filteredCases = cases.filter(c => 
    c.title?.includes(searchQuery) || 
    c.case_number?.includes(searchQuery) ||
    c.description?.includes(searchQuery)
  );

  const filteredConsultations = consultations.filter(c =>
    c.subject?.includes(searchQuery) ||
    c.message?.includes(searchQuery)
  );

  // أنواع القضايا
  const caseTypes = [
    'قضية جنائية',
    'قضية مدنية', 
    'قضية تجارية',
    'قضية أحوال شخصية',
    'قضية عمالية',
    'قضية إدارية',
    'قضية عقارية',
    'أخرى',
  ];

  // خدمات الموثق
  const notaryServices = [
    'توثيق عقود',
    'تصديق مستندات',
    'إقرارات',
    'وكالات شرعية',
    'عقود زواج',
    'عقود إيجار',
    'عقود بيع',
    'أخرى',
  ];

  // الخيارات الرئيسية
  const mainOptions = [
    {
      id: 'new-case',
      title: 'تسجيل قضية جديدة',
      description: 'تقديم طلب قضية جديدة للمكتب',
      icon: Plus,
      color: 'bg-green-500 hover:bg-green-600',
    },
    {
      id: 'inquiry',
      title: 'استعلام عن طلب سابق',
      description: 'متابعة حالة طلباتك السابقة',
      icon: Search,
      color: 'bg-blue-500 hover:bg-blue-600',
    },
    {
      id: 'consultation',
      title: 'طلب استشارة',
      description: 'احصل على استشارة قانونية من محامينا',
      icon: MessageSquare,
      color: 'bg-gold hover:bg-gold-dark',
    },
    {
      id: 'notary',
      title: 'خدمات الموثق',
      description: 'توثيق العقود والمستندات الرسمية',
      icon: PenTool,
      color: 'bg-purple-500 hover:bg-purple-600',
    },
  ];

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { text: 'قيد الانتظار', class: 'bg-yellow-100 text-yellow-700', icon: Clock },
      'in_progress': { text: 'قيد المعالجة', class: 'bg-blue-100 text-blue-700', icon: AlertCircle },
      'active': { text: 'نشط', class: 'bg-green-100 text-green-700', icon: CheckCircle },
      'completed': { text: 'مكتمل', class: 'bg-gray-100 text-gray-700', icon: CheckCircle },
      'responded': { text: 'تم الرد', class: 'bg-green-100 text-green-700', icon: CheckCircle },
    };
    return statusMap[status] || { text: status, class: 'bg-gray-100 text-gray-700', icon: Clock };
  };

  return (
    <div className="min-h-screen bg-sand" dir="rtl" lang="ar">
      {/* Mobile Menu Toggle */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 bg-primary text-white rounded-md"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed right-0 top-0 h-full bg-primary w-56 transition-transform duration-300 z-40 overflow-y-auto ${
          sidebarOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="p-4">
          <div className="flex items-center gap-2 mb-6">
            <Logo size="sm" />
            <h1 className="text-base font-heading font-bold text-white">
              بوابة العميل
            </h1>
          </div>

          <nav className="space-y-2">
            <button
              data-testid="menu-home"
              onClick={() => { setActiveView('main'); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-md transition-all duration-200 font-body text-sm ${
                activeView === 'main' ? 'bg-white/20 text-white' : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              <Home className="w-4 h-4" />
              <span>الرئيسية</span>
            </button>
            
            {mainOptions.map((option) => {
              const Icon = option.icon;
              return (
                <button
                  key={option.id}
                  data-testid={`menu-${option.id}`}
                  onClick={() => { setActiveView(option.id); setSidebarOpen(false); }}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-md transition-all duration-200 font-body text-sm ${
                    activeView === option.id ? 'bg-white/20 text-white' : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{option.title}</span>
                </button>
              );
            })}
          </nav>

          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="w-full mt-6 flex items-center gap-2 px-3 py-2 text-white bg-red-600 hover:bg-red-700 rounded-md transition-all duration-200 font-body text-sm"
          >
            <LogOut className="w-4 h-4" />
            <span>تسجيل الخروج</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:mr-56 p-6">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h2 className="text-2xl font-heading font-bold text-primary mb-1">
              مرحباً، {user?.full_name}
            </h2>
            <p className="text-sm text-gray-600 font-body">
              {activeView === 'main' && 'كيف يمكننا مساعدتك اليوم؟'}
              {activeView === 'new-case' && 'تسجيل قضية جديدة'}
              {activeView === 'inquiry' && 'استعلام عن طلباتك السابقة'}
              {activeView === 'consultation' && 'طلب استشارة قانونية'}
              {activeView === 'notary' && 'خدمات الموثق'}
            </p>
          </div>

          {/* Back Button */}
          {activeView !== 'main' && (
            <button
              onClick={() => setActiveView('main')}
              className="mb-6 flex items-center gap-2 text-primary hover:text-gold transition-colors font-body"
              data-testid="back-btn"
            >
              <ChevronLeft className="w-5 h-5" />
              <span>العودة للقائمة الرئيسية</span>
            </button>
          )}

          {/* Main Menu - 4 Options */}
          {activeView === 'main' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {mainOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <button
                    key={option.id}
                    data-testid={`option-${option.id}`}
                    onClick={() => setActiveView(option.id)}
                    className={`${option.color} text-white p-8 rounded-xl shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-xl text-right`}
                  >
                    <Icon className="w-12 h-12 mb-4" />
                    <h3 className="text-xl font-heading font-bold mb-2">{option.title}</h3>
                    <p className="text-sm font-body opacity-90">{option.description}</p>
                  </button>
                );
              })}
            </div>
          )}

          {/* New Case Form */}
          {activeView === 'new-case' && (
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-green-100 rounded-lg">
                  <Briefcase className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-xl font-heading font-bold text-primary">تسجيل قضية جديدة</h3>
                  <p className="text-sm text-gray-500 font-body">يرجى تعبئة جميع البيانات المطلوبة</p>
                </div>
              </div>

              <form onSubmit={submitNewCase} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-primary mb-2 font-body">
                    نوع القضية <span className="text-red-500">*</span>
                  </label>
                  <select
                    data-testid="case-type-select"
                    value={caseForm.case_type}
                    onChange={(e) => setCaseForm({ ...caseForm, case_type: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold outline-none font-body"
                  >
                    <option value="">اختر نوع القضية</option>
                    {caseTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-2 font-body">
                    عنوان القضية <span className="text-red-500">*</span>
                  </label>
                  <input
                    data-testid="case-title-input"
                    type="text"
                    value={caseForm.title}
                    onChange={(e) => setCaseForm({ ...caseForm, title: e.target.value })}
                    required
                    placeholder="مثال: قضية مطالبة مالية"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold outline-none font-body"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-2 font-body">
                    وصف القضية <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    data-testid="case-description-input"
                    value={caseForm.description}
                    onChange={(e) => setCaseForm({ ...caseForm, description: e.target.value })}
                    required
                    rows={4}
                    placeholder="اشرح تفاصيل القضية..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold outline-none font-body"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-primary mb-2 font-body">
                      اسم الخصم
                    </label>
                    <input
                      data-testid="opponent-name-input"
                      type="text"
                      value={caseForm.opponent_name}
                      onChange={(e) => setCaseForm({ ...caseForm, opponent_name: e.target.value })}
                      placeholder="اسم الطرف الآخر"
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold outline-none font-body"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-primary mb-2 font-body">
                      المحكمة
                    </label>
                    <input
                      data-testid="court-name-input"
                      type="text"
                      value={caseForm.court_name}
                      onChange={(e) => setCaseForm({ ...caseForm, court_name: e.target.value })}
                      placeholder="اسم المحكمة إن وجد"
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold outline-none font-body"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-2 font-body">
                    رقم الهوية <span className="text-red-500">*</span>
                  </label>
                  <input
                    data-testid="id-number-input"
                    type="text"
                    value={caseForm.id_number}
                    onChange={(e) => setCaseForm({ ...caseForm, id_number: e.target.value })}
                    required
                    placeholder="رقم الهوية الوطنية"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold outline-none font-body"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="submit-case-btn"
                  className="w-full py-4 bg-green-500 text-white rounded-lg font-bold hover:bg-green-600 transition-all disabled:opacity-50 font-body"
                >
                  {loading ? 'جاري الإرسال...' : 'إرسال الطلب'}
                </button>
              </form>
            </div>
          )}

          {/* Inquiry View */}
          {activeView === 'inquiry' && (
            <div className="space-y-6">
              {/* Search Box */}
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <Search className="w-5 h-5 text-gray-400" />
                  <input
                    data-testid="search-input"
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="ابحث برقم الطلب أو العنوان..."
                    className="flex-1 px-4 py-2 border-0 focus:ring-0 outline-none font-body"
                  />
                </div>
              </div>

              {/* Cases List */}
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-lg font-heading font-bold text-primary mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  قضاياي ({filteredCases.length})
                </h3>
                {filteredCases.length > 0 ? (
                  <div className="space-y-3">
                    {filteredCases.map((c) => {
                      const status = getStatusBadge(c.status);
                      const StatusIcon = status.icon;
                      return (
                        <div
                          key={c.id}
                          className="flex items-center justify-between p-4 bg-sand rounded-lg hover:bg-gray-100 transition-colors"
                          data-testid={`inquiry-case-${c.id}`}
                        >
                          <div>
                            <p className="font-semibold text-primary font-body">{c.title}</p>
                            <p className="text-sm text-gray-500 font-body">رقم القضية: {c.case_number}</p>
                            <p className="text-xs text-gray-400 font-body">{c.case_type}</p>
                          </div>
                          <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-body ${status.class}`}>
                            <StatusIcon className="w-4 h-4" />
                            {status.text}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8 font-body">لا توجد قضايا مسجلة</p>
                )}
              </div>

              {/* Consultations List */}
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-lg font-heading font-bold text-primary mb-4 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  استشاراتي ({filteredConsultations.length})
                </h3>
                {filteredConsultations.length > 0 ? (
                  <div className="space-y-3">
                    {filteredConsultations.map((c) => {
                      const status = getStatusBadge(c.status);
                      const StatusIcon = status.icon;
                      return (
                        <div
                          key={c.id}
                          className="p-4 bg-sand rounded-lg"
                          data-testid={`inquiry-consultation-${c.id}`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <p className="font-semibold text-primary font-body">{c.subject}</p>
                            <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-body ${status.class}`}>
                              <StatusIcon className="w-4 h-4" />
                              {status.text}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 font-body mb-2">{c.message}</p>
                          {c.response && (
                            <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                              <p className="text-sm font-medium text-green-700 mb-1">الرد:</p>
                              <p className="text-sm text-green-800 font-body">{c.response}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8 font-body">لا توجد استشارات مسجلة</p>
                )}
              </div>
            </div>
          )}

          {/* Consultation Form */}
          {activeView === 'consultation' && (
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-gold/20 rounded-lg">
                  <MessageSquare className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <h3 className="text-xl font-heading font-bold text-primary">طلب استشارة قانونية</h3>
                  <p className="text-sm text-gray-500 font-body">سيقوم أحد محامينا بالرد عليك في أقرب وقت</p>
                </div>
              </div>

              <form onSubmit={submitConsultation} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-primary mb-2 font-body">
                    موضوع الاستشارة <span className="text-red-500">*</span>
                  </label>
                  <input
                    data-testid="consultation-subject-input"
                    type="text"
                    value={consultationForm.subject}
                    onChange={(e) => setConsultationForm({ ...consultationForm, subject: e.target.value })}
                    required
                    placeholder="مثال: استشارة حول عقد عمل"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold outline-none font-body"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-2 font-body">
                    تفاصيل الاستشارة <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    data-testid="consultation-message-input"
                    value={consultationForm.message}
                    onChange={(e) => setConsultationForm({ ...consultationForm, message: e.target.value })}
                    required
                    rows={6}
                    placeholder="اشرح استفسارك بالتفصيل..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold outline-none font-body"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="submit-consultation-btn"
                  className="w-full py-4 bg-gold text-white rounded-lg font-bold hover:bg-gold-dark transition-all disabled:opacity-50 font-body"
                >
                  {loading ? 'جاري الإرسال...' : 'إرسال طلب الاستشارة'}
                </button>
              </form>
            </div>
          )}

          {/* Notary Services Form */}
          {activeView === 'notary' && (
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <PenTool className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-xl font-heading font-bold text-primary">خدمات الموثق</h3>
                  <p className="text-sm text-gray-500 font-body">توثيق العقود والمستندات الرسمية</p>
                </div>
              </div>

              <form onSubmit={submitNotaryRequest} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-primary mb-2 font-body">
                    نوع الخدمة <span className="text-red-500">*</span>
                  </label>
                  <select
                    data-testid="notary-service-select"
                    value={notaryForm.service_type}
                    onChange={(e) => setNotaryForm({ ...notaryForm, service_type: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold outline-none font-body"
                  >
                    <option value="">اختر نوع الخدمة</option>
                    {notaryServices.map((service) => (
                      <option key={service} value={service}>{service}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-2 font-body">
                    الأطراف المعنية
                  </label>
                  <input
                    data-testid="notary-parties-input"
                    type="text"
                    value={notaryForm.parties_involved}
                    onChange={(e) => setNotaryForm({ ...notaryForm, parties_involved: e.target.value })}
                    placeholder="أسماء الأطراف المعنية بالتوثيق"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold outline-none font-body"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-2 font-body">
                    تفاصيل الطلب <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    data-testid="notary-description-input"
                    value={notaryForm.description}
                    onChange={(e) => setNotaryForm({ ...notaryForm, description: e.target.value })}
                    required
                    rows={5}
                    placeholder="اشرح تفاصيل الخدمة المطلوبة..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold outline-none font-body"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="submit-notary-btn"
                  className="w-full py-4 bg-purple-500 text-white rounded-lg font-bold hover:bg-purple-600 transition-all disabled:opacity-50 font-body"
                >
                  {loading ? 'جاري الإرسال...' : 'إرسال طلب التوثيق'}
                </button>
              </form>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
